/**
 * SCP Feature Carousel
 * Handles carousel functionality, variant selection, and add to cart
 */

// Diagnostic logging gated behind a single flag so logs stay silent in
// production. Flip it on from DevTools with:
//   window.SCP_ATC_DEBUG = true; location.reload();
// Logs are read at call time so the flag can also be flipped without reload.
// Convenience: visiting any page with `?scp_debug=1` auto-enables the flag
// so audits can be done without opening DevTools first.
if (
  typeof window !== "undefined" &&
  typeof window.location !== "undefined" &&
  typeof window.location.search === "string" &&
  window.location.search.includes("scp_debug=1")
) {
  window.SCP_ATC_DEBUG = true;
}
const scpLog = (...args) => {
  if (typeof window !== "undefined" && window.SCP_ATC_DEBUG === true) {
    //console.log("[SCP ATC]", ...args);
  }
};

// Document-level capture-phase logger for any click on a
// `.scp-product-card__quick-add-button`. Sees the click before any
// stopPropagation downstream, so we can prove the click reached the page
// even when no per-instance/delegated handler runs. Read defaultPrevented
// /propagationStopped in a microtask so we capture state AFTER any
// downstream listeners (which run after this capture-phase fires).
if (typeof document !== "undefined" && !document.scpButtonClickLoggerBound) {
  document.scpButtonClickLoggerBound = true;
  document.addEventListener(
    "click",
    (event) => {
      if (
        typeof window === "undefined" ||
        window.SCP_ATC_DEBUG !== true ||
        !event.target ||
        typeof event.target.closest !== "function"
      ) {
        return;
      }
      const button = event.target.closest(".scp-product-card__quick-add-button");
      if (!button) return;

      const nested = button.closest("[data-scp-product-card]");
      const featureCard = button.closest(".scp-feature-card");
      const media = button.closest(".m-product-card__media");
      const instance = nested && nested.scpProductCard;

      const payload = {
        productId: button.dataset && button.dataset.productId,
        hasDataQuickAddTrigger: button.hasAttribute("data-quick-add-trigger"),
        inFeatureCarousel: !!button.closest("scp-feature-carousel"),
        inSwiperDuplicate: !!button.closest(".swiper-slide-duplicate"),
        cardHasInstance: !!instance,
        instanceOverlayPresent: !!(instance && instance.variantOverlay),
        instanceTriggerCount:
          instance && instance.quickAddTriggers
            ? instance.quickAddTriggers.length
            : null,
        targetTag: event.target.tagName,
        targetClasses:
          (event.target.className && String(event.target.className)) || "",
        triggerComputedPointerEvents:
          typeof getComputedStyle === "function"
            ? getComputedStyle(button).pointerEvents
            : null,
        mediaComputedPointerEvents:
          media && typeof getComputedStyle === "function"
            ? getComputedStyle(media).pointerEvents
            : null,
        featureCardClasses: featureCard ? featureCard.className : null,
      };
      scpLog("button-click", payload);

      // Read post-dispatch state in a microtask so any per-instance handler
      // bound in bubble phase has had a chance to run and call
      // preventDefault/stopPropagation. The same event object is observable
      // here even after dispatch.
      Promise.resolve().then(() => {
        scpLog("button-click:post", {
          productId: payload.productId,
          defaultPrevented: event.defaultPrevented,
          // No standard API for "propagation stopped" but we surface the
          // public property for completeness.
          eventPhase: event.eventPhase,
        });
      });
    },
    true,
  );
}

if (!customElements.get("scp-feature-carousel")) {
  class SCPFeatureCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.activeSlideIndex = 0;
      this.enableActiveCard = this.dataset.enableActiveCard === "true";
      this.documentListeners = [];
      this.isMobile = window.innerWidth < 990;
      this.resizeHandler = null;
      this._wheelCleanup = null;
      this._wheelEndTimer = null;
      this._wheelAutoplayPaused = false;
      this._pendingTimeouts = [];
      this._scpAlignTimer = null;
      this._initRetryCount = 0;
      this._maxInitRetries = 50;
    }

    connectedCallback() {
      // Update mobile state
      this.isMobile = window.innerWidth < 990;

      // If active card is disabled OR on mobile, remove any active classes
      if (!this.enableActiveCard || this.isMobile) {
        this.removeAllActiveClasses();
      } else {
        // Set initial active state before Swiper initializes (desktop only)
        this.setInitialActiveCard();
      }

      // Setup resize handler for viewport transitions
      this.setupResizeHandler();

      this.initCarousel();
      // initProductCards() is now called after Swiper init (see initCarousel's on.init callback)
    }

    /**
     * Remove all active classes from cards (used when active card is disabled)
     */
    removeAllActiveClasses() {
      const allCards = this.querySelectorAll(".scp-feature-card");
      allCards.forEach((card) => {
        if (card.classList.contains("scp-feature-card--active")) {
          card.classList.remove("scp-feature-card--active");
        }
      });
    }

    /**
     * Set the initial active card before Swiper initialization
     */
    setInitialActiveCard() {
      const firstSlide = this.querySelector(".scp-feature-carousel__slide");
      if (firstSlide) {
        const firstCard = firstSlide.querySelector(".scp-feature-card");
        if (firstCard) {
          if (!firstCard.classList.contains("scp-feature-card--active")) {
            firstCard.classList.add("scp-feature-card--active");
          }
          // Direction 0 keeps the active face at translateX(0) on the very
          // first paint so it appears statically without sliding in.
          firstCard.style.setProperty("--scp-face-dir", "0");
        }
      }
    }

    /**
     * Debounce helper function
     */
    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }

    /**
     * Setup resize handler for viewport transitions
     */
    setupResizeHandler() {
      this.resizeHandler = this.debounce(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 990;

        // Only handle viewport transitions
        if (wasMobile !== this.isMobile) {
          this.handleViewportChange();
        }
      }, 150);

      window.addEventListener("resize", this.resizeHandler);
    }

    /**
     * Handle viewport change between mobile and desktop
     *
     * Swiper cannot reliably toggle the `loop` parameter via responsive
     * breakpoints — it requires a full destroy + reinit. Without this, the
     * desktop view ends up without duplicate slides (loop visually broken)
     * after a mobile -> desktop resize, and vice versa leaks duplicates.
     */
    handleViewportChange() {
      const toMobile = this.isMobile;

      if (toMobile) {
        this.removeAllActiveClasses();
      }

      if (!this.swiper || this.swiper.destroyed) {
        if (this.enableActiveCard && !toMobile) {
          this.setInitialActiveCard();
        }
        this.initCarousel();
        return;
      }

      // Preserve the currently visible slide across the reinit.
      const preservedIndex = toMobile
        ? 0
        : (typeof this.swiper.realIndex === "number"
            ? this.swiper.realIndex
            : 0);
      this._pendingInitialSlide = preservedIndex;

      this.detachWheelSlideScroll();
      this.swiper.destroy(true, true);
      this.swiper = null;

      // The destroy(true, true) call removes Swiper-added classes; the
      // visibility-hiding rule keys off `.scp-swiper-ready` so the inner
      // wrapper would flash hidden until init finishes. Add it pre-emptively;
      // initCarousel will (re)set it after Swiper init completes.
      const container = this.querySelector(".swiper-container");
      if (container) {
        container.classList.add("scp-swiper-ready");
      }

      if (this.enableActiveCard && !toMobile) {
        this.setInitialActiveCard();
      }

      this.initCarousel();
    }

    attachWheelSlideScroll(container) {
      this.detachWheelSlideScroll();
      if (!container) return;

      // Continuous free-translate model: each wheel event nudges the swiper's
      // translate by the gesture delta so a two-finger trackpad swipe tracks
      // the fingers like horizontal page scroll. After ~140 ms of wheel
      // silence we treat the gesture as ended and snap to the closest card.
      // The bundled swiper.js does NOT include the Mousewheel module, so we
      // implement this by hand; touch swipes use Swiper's freeMode + sticky.
      const wheelEndDelayMs = 140;

      // Snap to the closest slide using slideToClosest if the bundle exposes
      // it; otherwise fall back to scanning swiper.snapGrid for the nearest
      // anchor. snapGrid is always populated when freeMode is on.
      const snapToClosest = () => {
        if (!this.swiper || this.swiper.destroyed) return;
        const swiper = this.swiper;
        const speed = (swiper.params && swiper.params.speed) || 300;
        if (typeof swiper.slideToClosest === "function") {
          swiper.slideToClosest(speed);
          return;
        }
        const grid = swiper.snapGrid || [];
        if (!grid.length) return;
        const current = swiper.translate;
        let nearestIdx = 0;
        let nearestDist = Math.abs(current - grid[0]);
        for (let i = 1; i < grid.length; i++) {
          const dist = Math.abs(current - grid[i]);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearestIdx = i;
          }
        }
        swiper.slideTo(nearestIdx, speed);
      };

      const finishGesture = () => {
        this._wheelEndTimer = null;
        snapToClosest();
        // We call updateActiveIndex() inside the wheel loop to keep loopFix
        // in sync, which means slideToClosest above often resolves to an
        // index Swiper already sat on -> slideChange and
        // slideChangeTransitionEnd do NOT fire, so the active class +
        // parallel translate never run. Trigger them explicitly here.
        // handleSlideChange is idempotent: if a real
        // slideChangeTransitionEnd does fire later it will simply reapply
        // the same class and re-target the same translate.
        if (this.swiper && !this.swiper.destroyed) {
          this.handleSlideChange(this.swiper);
        }
        if (this._wheelAutoplayPaused) {
          this._wheelAutoplayPaused = false;
          if (
            this.swiper &&
            this.swiper.autoplay &&
            typeof this.swiper.autoplay.resume === "function"
          ) {
            this.swiper.autoplay.resume();
          }
        }
      };

      const handler = (e) => {
        if (!this.swiper || this.swiper.destroyed) return;

        // Pick horizontal intent: prefer deltaX, fall back to deltaY only when
        // shift is held (classic mouse-wheel "horizontal scroll" affordance).
        let scrollDelta;
        if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) {
          scrollDelta = e.deltaX;
        } else if (e.shiftKey) {
          scrollDelta = e.deltaY;
        } else {
          // Vertical-dominant gesture without shift: let the page handle it.
          return;
        }

        if (Math.abs(scrollDelta) < 1) {
          return;
        }

        const swiper = this.swiper;
        const isLoop = !!(swiper.params && swiper.params.loop);

        // At a non-loop boundary, do nothing and let the page consume the
        // gesture rather than swallowing it with preventDefault.
        if (
          !isLoop &&
          ((swiper.isEnd && scrollDelta > 0) ||
            (swiper.isBeginning && scrollDelta < 0))
        ) {
          return;
        }

        e.preventDefault();

        // Pause autoplay for the duration of the gesture so the wheel and the
        // autoplay tick do not fight over the translate. Resumed in
        // finishGesture once the snap completes.
        if (
          !this._wheelAutoplayPaused &&
          swiper.autoplay &&
          typeof swiper.autoplay.pause === "function"
        ) {
          swiper.autoplay.pause();
          this._wheelAutoplayPaused = true;
        }

        // Translate is negative as we move forward (LTR); subtracting the
        // wheel delta moves the strip in the natural direction of the gesture.
        let next = swiper.translate - scrollDelta;
        if (!isLoop) {
          const minT = swiper.minTranslate();
          const maxT = swiper.maxTranslate();
          if (next > minT) next = minT;
          if (next < maxT) next = maxT;
        }

        swiper.setTransition(0);
        swiper.setTranslate(next);
        if (typeof swiper.updateProgress === "function") {
          swiper.updateProgress();
        }
        if (typeof swiper.updateActiveIndex === "function") {
          swiper.updateActiveIndex();
        }
        if (isLoop && typeof swiper.loopFix === "function") {
          // Keep the wrap-around seamless when the user scrolls past the
          // duplicates Swiper inserts at the head/tail of the loop.
          swiper.loopFix();
        }

        if (this._wheelEndTimer) clearTimeout(this._wheelEndTimer);
        this._wheelEndTimer = setTimeout(finishGesture, wheelEndDelayMs);
      };

      container.addEventListener("wheel", handler, { passive: false });
      this._wheelCleanup = () => {
        container.removeEventListener("wheel", handler);
        this._wheelCleanup = null;
        if (this._wheelEndTimer) {
          clearTimeout(this._wheelEndTimer);
          this._wheelEndTimer = null;
        }
        if (this._wheelAutoplayPaused) {
          this._wheelAutoplayPaused = false;
          if (
            this.swiper &&
            !this.swiper.destroyed &&
            this.swiper.autoplay &&
            typeof this.swiper.autoplay.resume === "function"
          ) {
            this.swiper.autoplay.resume();
          }
        }
      };
    }

    detachWheelSlideScroll() {
      if (typeof this._wheelCleanup === "function") {
        this._wheelCleanup();
      }
    }

    /**
     * Initialize Swiper carousel
     */
    initCarousel() {
      const container = this.querySelector(".swiper-container");
      if (!container) {
        return;
      }

      // Check if MinimogLibs.Swiper is available
      if (
        typeof MinimogLibs === "undefined" ||
        typeof MinimogLibs.Swiper === "undefined"
      ) {
        // Retry after a short delay with max attempts
        this._initRetryCount++;
        if (this._initRetryCount > this._maxInitRetries) {
          console.warn(
            "[SCP Feature Carousel] Swiper library not available after max retries",
          );
          // Fallback: reveal the inner wrapper as a horizontally-scrollable
          // strip so users can still see and reach the products without JS.
          // Without this, .scp-feature-carousel__inner stays visibility:hidden.
          container.classList.add("scp-swiper-ready", "scp-swiper-fallback");
          return;
        }
        const timeoutId = setTimeout(() => this.initCarousel(), 100);
        this._pendingTimeouts.push(timeoutId);
        return;
      }

      const autoplay = this.dataset.autoplay === "true";
      const autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 10000;
      const showNavigation = this.dataset.showNavigation === "true";
      const showPagination = this.dataset.showPagination === "true";

      // Resolve the slide to land on. Defaults to 0; reuses _pendingInitialSlide
      // when the carousel is being reinitialised (e.g., breakpoint crossing).
      const initialSlide =
        typeof this._pendingInitialSlide === "number"
          ? this._pendingInitialSlide
          : 0;
      this._pendingInitialSlide = null;

      // We need cross-breakpoint awareness for which loop/spacing to use.
      // Because we destroy + reinit on breakpoint change, set the correct
      // values on the base config (not just the responsive breakpoint).
      const isDesktop = !this.isMobile;

      const swiperConfig = {
        slidesPerView: "auto",
        spaceBetween: isDesktop ? 24 : 15,
        loop: isDesktop,
        loopedSlides: isDesktop ? 12 : undefined,
        watchOverflow: true,
        // Continuous physics-based scrolling for both touch and mouse-drag.
        // sticky:true makes Swiper snap to the nearest slide on release so
        // a short flick lands on the next card and a long sweep coasts
        // across multiple cards before clicking into place.
        // momentumBounce:false avoids the rubber-band overshoot at non-loop
        // boundaries on mobile.
        freeMode: {
          enabled: true,
          sticky: true,
          momentumBounce: false,
          momentumRatio: 1,
          momentumVelocityRatio: 1,
          minimumVelocity: 0.02,
        },
        centeredSlides: false,
        // Mobile: rely on the .container-fluid 15 px left padding so the first
        // card aligns with the heading above. The right side has no container
        // padding (the wrapper sets padding-right:0px !important) so we keep a
        // 16 px after-offset to give the last card breathing room.
        slidesOffsetBefore: 0,
        slidesOffsetAfter: isDesktop ? 0 : 16,
        initialSlide,
        observer: true,
        observeParents: true,
        // Touch configuration to work with nested carousels
        touchEventsTarget: "wrapper",
        simulateTouch: true,
        allowTouchMove: true,
        threshold: 10,
        touchStartPreventDefault: false,
        nested: false,
        on: {
          slideChangeTransitionEnd: (swiper) => {
            this.handleSlideChange(swiper);
          },
          // realIndexChange intentionally NOT bound: it fires continuously
          // during free-mode trackpad/wheel scrolling, which previously caused
          // every card the strip passed to briefly receive the active class
          // and triggered swiper.update() against in-transition widths,
          // producing snap-grid drift and apparent card-skipping. Loop
          // boundary crossings still settle via slideChangeTransitionEnd.
          init: (swiper) => {
            // Liquid pre-marks the first card active for SSR, and Swiper
            // clones every slide for loop mode so every clone inherits that
            // class. We must strip active from clones/wrong cards BEFORE
            // `.scp-swiper-ready` is added (which unhides the strip), but
            // stripping-then-readding on the same correct card fires the
            // card's `width/height/background` CSS transitions and produces
            // a visible flicker on first paint. The shared helper strips only
            // from cards that are NOT the resolved target, so the SSR-marked
            // card's class stays continuous (no transition flicker).
            this.applyActiveCardForSwiper(swiper);

            requestAnimationFrame(() => {
              if (swiper.destroyed) return;
              this.handleSlideChange(swiper);
            });

            const initCardsTimeout = setTimeout(() => {
              this.initProductCards();
            }, 50);
            this._pendingTimeouts.push(initCardsTimeout);
          },
          // Swiper's loop helpers and the observer (observer/observeParents)
          // can re-clone or reorder slides after init. Without re-running
          // the strip, a clone of the SSR-active card surfaces with the
          // `--active` class still attached, which CSS-blocks pointer events
          // on its inactive face (the quick-add trigger) and produces the
          // "ATC button does nothing on non-active cards" symptom. Re-strip
          // on every resync so stale `--active` clones can never persist.
          loopFix: (swiper) => {
            this.applyActiveCardForSwiper(swiper);
            // Swiper can spawn fresh `swiper-slide-duplicate` elements during
            // loopFix that were not present at the original `initProductCards`
            // pass. Re-running it (debounced) lets makeCloneUnique + every
            // per-card init handler claim those new clones; the
            // `scpCloneUniquified` and `dataset.*Initialized` guards make
            // re-runs no-ops for already-handled cards.
            this.scheduleClonesReinit();
          },
          update: (swiper) => {
            this.applyActiveCardForSwiper(swiper);
            this.scheduleClonesReinit();
          },
          observerUpdate: (swiper) => {
            this.applyActiveCardForSwiper(swiper);
            this.scheduleClonesReinit();
          },
          breakpoint: (swiper) => {
            this.applyActiveCardForSwiper(swiper);
          },
        },
      };

      // Add autoplay if enabled
      if (autoplay) {
        swiperConfig.autoplay = {
          delay: autoplaySpeed,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        };

        // Pause autoplay while a finger is on the carousel so the slide
        // does not jump mid-swipe. Resume on touchEnd / cancel.
        const pauseAutoplay = (swiper) => {
          if (swiper && swiper.autoplay && typeof swiper.autoplay.pause === "function") {
            swiper.autoplay.pause();
          }
        };
        const resumeAutoplay = (swiper) => {
          if (swiper && swiper.autoplay && typeof swiper.autoplay.resume === "function") {
            swiper.autoplay.resume();
          }
        };
        swiperConfig.on.touchStart = pauseAutoplay;
        swiperConfig.on.touchEnd = resumeAutoplay;
        swiperConfig.on.touchMoveOpposite = resumeAutoplay;
      }

      // Add navigation if enabled
      if (showNavigation) {
        // Look for navigation buttons in parent container as well
        const nextBtn =
          this.querySelector(".swiper-button-next") ||
          this.parentElement?.querySelector(".swiper-button-next");
        const prevBtn =
          this.querySelector(".swiper-button-prev") ||
          this.parentElement?.querySelector(".swiper-button-prev");

        if (nextBtn && prevBtn) {
          swiperConfig.navigation = {
            nextEl: nextBtn,
            prevEl: prevBtn,
          };
        }
      }

      // Add pagination if enabled
      if (showPagination) {
        // Look for pagination in parent container as well
        const paginationEl =
          this.querySelector(".swiper-pagination") ||
          this.parentElement?.querySelector(".swiper-pagination");

        if (paginationEl) {
          swiperConfig.pagination = {
            el: paginationEl,
            clickable: true,
            dynamicBullets: true,
          };
        }
      }

      // Use MinimogLibs.Swiper instead of global Swiper
      this.swiper = new MinimogLibs.Swiper(container, swiperConfig);
      this.attachWheelSlideScroll(container);
      container.classList.add("scp-swiper-ready");

      scpLog("init:swiper", {
        sectionId: this.dataset.sectionId || this.closest("[data-section-id]")?.dataset.sectionId,
        isMobile: this.isMobile,
        loop: swiperConfig.loop,
        slides: this.querySelectorAll(".swiper-slide").length,
        loopedSlides: swiperConfig.loopedSlides,
      });

      // Force update after initialization to ensure proper layout with active card
      const updateTimeout = setTimeout(() => {
        if (this.swiper && !this.swiper.destroyed) {
          this.swiper.update();
          // Re-run the strip after the post-init layout settle: the
          // observer/loopFix events fired during boot may have created
          // additional clones that the on.init pass did not see.
          this.applyActiveCardForSwiper(this.swiper);
        }
      }, 100);
      this._pendingTimeouts.push(updateTimeout);
    }

    /**
     * Strip residual `.scp-feature-card--active` from any card that is not the
     * Swiper-resolved target slide and (re)apply it to the target. Safe to
     * call repeatedly; idempotent. Used from `on.init`, `on.loopFix`,
     * `on.update`, `on.observerUpdate`, `on.breakpoint`, and the post-init
     * settle timeout so clones that materialise after the first init pass
     * cannot leave their inactive face CSS-blocked (pointer-events: none).
     */
    applyActiveCardForSwiper(swiper) {
      if (!swiper || swiper.destroyed) return;

      // Mobile is always single-face (the nested scp-product-card). Liquid
      // pre-applies --active to the first card for SSR; connectedCallback and
      // handleSlideChange (mobile branch) strip it, but Swiper's
      // init/loopFix/update/observerUpdate/breakpoint events would otherwise
      // re-add it here, hiding the inactive face on the first slide and
      // producing an empty white card (the active-face mobile layout is not a
      // supported presentation; see assets/scp-feature-carousel.css:1751-1791
      // which gates every mobile rule on :not(.scp-feature-card--active)).
      if (this.isMobile) {
        this.removeAllActiveClasses();
        return;
      }

      const targetSlide = this.enableActiveCard
        ? swiper.slides && swiper.slides[swiper.activeIndex]
        : null;
      const targetCard = targetSlide
        ? targetSlide.querySelector(".scp-feature-card")
        : null;

      this.querySelectorAll(".scp-feature-card--active").forEach((card) => {
        if (card !== targetCard) {
          card.classList.remove("scp-feature-card--active");
        }
      });
      if (
        targetCard &&
        !targetCard.classList.contains("scp-feature-card--active")
      ) {
        targetCard.classList.add("scp-feature-card--active");
      }
      if (targetCard) {
        targetCard.style.setProperty("--scp-face-dir", "0");
      }
    }

    /**
     * Handle slide change - update active card and let Swiper position natively
     */
    handleSlideChange(swiper) {
      if (this.isMobile) {
        this.removeAllActiveClasses();
        return;
      }

      if (this.enableActiveCard) {
        // Resolve the new active card up-front so we can both flip the class
        // and set --scp-face-dir on the cards whose face state is changing.
        const newActiveSlide = swiper.slides[swiper.activeIndex];
        const newActiveCard = newActiveSlide
          ? newActiveSlide.querySelector(".scp-feature-card")
          : null;
        const flipping = new Set();
        this.querySelectorAll(".scp-feature-card--active").forEach((c) => {
          if (c !== newActiveCard) flipping.add(c);
        });
        if (
          newActiveCard &&
          !newActiveCard.classList.contains("scp-feature-card--active")
        ) {
          flipping.add(newActiveCard);
        }

        // Direction = +1 forward (carousel moved forward, face slides in
        // from the right), -1 backward (face slides in from the left).
        // Modular distance handles loop wrap correctly so a wrap-around
        // forward swipe doesn't read as a long backward jump.
        const total = swiper.slides ? swiper.slides.length : 0;
        const prevReal =
          typeof swiper.previousRealIndex === "number"
            ? swiper.previousRealIndex
            : this.activeSlideIndex;
        const nextReal = swiper.realIndex;
        let dir = 0;
        if (total > 0 && nextReal !== prevReal) {
          const forwardDist = (nextReal - prevReal + total) % total;
          const backwardDist = (prevReal - nextReal + total) % total;
          dir = forwardDist <= backwardDist ? 1 : -1;
        }
        if (dir !== 0 && flipping.size > 0) {
          // Snap face transforms to the new dir's parked positions WITHOUT
          // transition, then re-enable transitions before toggling --active
          // below. Otherwise both start and end positions update in the
          // same frame and the browser only animates opacity (no slide).
          flipping.forEach((c) => {
            c.classList.add("scp-feature-card--snap-face");
            c.style.setProperty("--scp-face-dir", String(dir));
          });
          // Force reflow so the snapped transform applies before the next
          // style change re-enables transitions.
          void this.offsetWidth;
          flipping.forEach((c) => {
            c.classList.remove("scp-feature-card--snap-face");
          });
        }

        const allSlides = this.querySelectorAll(".scp-feature-carousel__slide");
        const realIndex = swiper.realIndex;

        allSlides.forEach((slide) => {
          const card = slide.querySelector(".scp-feature-card");
          if (card) {
            card.classList.remove("scp-feature-card--active");
          }
        });

        let activeCard = null;
        if (newActiveSlide) {
          activeCard = newActiveCard;
          if (activeCard) {
            activeCard.classList.add("scp-feature-card--active");
          }
        }
        this.activeSlideIndex = realIndex;

        // The active card grows from --card-width to --card-active-width over
        // ~350 ms (CSS transition). Swiper's current translate was computed
        // for the previous layout (previous card wide, new card narrow), so
        // by the time the size transition settles the snap grid has shifted
        // by ~(cardActiveWidth - cardWidth) and the active card is no longer
        // aligned. Animate Swiper's translate IN PARALLEL with the size
        // transition, targeting the future snap point computed from known
        // widths. Both motions end together, perfectly aligned.
        const computed = getComputedStyle(this);
        const cardWidth =
          parseFloat(computed.getPropertyValue("--card-width")) || 312;
        const spaceBetween = swiper.params.spaceBetween || 0;
        const offsetBefore = swiper.params.slidesOffsetBefore || 0;

        // Only slides[activeIndex] is wide; all others (incl. clones) are
        // narrow. Walk preceding slides as narrow + spaceBetween.
        let targetTranslate = -offsetBefore;
        for (let i = 0; i < swiper.activeIndex; i++) {
          targetTranslate -= cardWidth + spaceBetween;
        }
        if (!swiper.params.loop) {
          const minT = swiper.minTranslate ? swiper.minTranslate() : 0;
          const maxT = swiper.maxTranslate
            ? swiper.maxTranslate()
            : targetTranslate;
          targetTranslate = Math.max(maxT, Math.min(minT, targetTranslate));
        }

        const ANIM_MS = 350;
        swiper.setTransition(ANIM_MS);
        swiper.setTranslate(targetTranslate);

        clearTimeout(this._scpAlignTimer);
        this._scpAlignTimer = setTimeout(() => {
          this._scpAlignTimer = null;
          if (!this.swiper || this.swiper.destroyed) return;
          this.swiper.setTransition(0);
          this.swiper.update();
        }, ANIM_MS + 30);
        this._pendingTimeouts.push(this._scpAlignTimer);
      }
    }

    /**
     * Rewrite every duplicated identifier inside a Swiper-clone feature
     * card so the clone can stand on its own (own form, own radio group,
     * own label-for resolution) instead of silently sharing state with
     * the original slide. Called once per clone from `initProductCards`
     * BEFORE the per-clone init* handlers bind, because those handlers
     * cache references to inputs/overlays during binding.
     *
     * Covers four classes of collision:
     * 1. `<form id="scp-product-form-...">` from
     *    snippets/scp-feature-product-card.liquid line 367.
     * 2. `<input form="...">` references in
     *    snippets/scp-feature-card-sizes.liquid line 44 — without rewriting
     *    these, the HTML form-association API routes the clone's selected
     *    size to the ORIGINAL form on submit.
     * 3. `<input id>` + `<label for>` pairs in
     *    snippets/scp-feature-card-sizes.liquid (line 41/53) — id must be
     *    document-unique so label-for resolution finds the right radio
     *    inside the clone. Note: the per-face .scp-product-card__variant-overlay
     *    overlays (rendered inside .m-product-card__media on the inactive
     *    face and inside .scp-feature-card__media on the active face) both
     *    render snippets/scp-product-card-sizes, which emits only
     *    <button data-size-option> nodes — no id, name or form attributes —
     *    so they add nothing to the rewrite surface.
     * 4. `<input type="radio" name="...">` groups — radios with identical
     *    `name` form a single mutually-exclusive group across the entire
     *    document, so a click on a clone's "M" radio would deselect the
     *    original's "M" radio. Cross-clone size sync stays intact because
     *    handleSizeChange iterates every matching `[data-product-id]` and
     *    updates each card explicitly via JS.
     *
     * @param {Element} card    The cloned `.scp-feature-card` element.
     * @param {string} suffix  Suffix to append (e.g. "duplicate-1").
     * @returns {string|null}   The clone's new form id (or null if no form).
     */
    makeCloneUnique(card, suffix) {
      // Idempotency guard. `initProductCards()` is now re-run whenever
      // Swiper rebuilds clones (loopFix / update / observerUpdate), and
      // running this rewrite a second time on the same card would
      // double-suffix every id (`...-duplicate-1-duplicate-1`),
      // breaking form-association and label-for resolution. Return the
      // existing rewritten form id so callers that store it still work.
      if (card.dataset.scpCloneUniquified === "true") {
        const existingForm = card.querySelector(".scp-product-form__form");
        return existingForm && existingForm.id ? existingForm.id : null;
      }

      // 1. Form id rewrite. Capture the originalId so step 2 can repoint
      //    `<input form="originalId">` references at the new id.
      const form = card.querySelector(".scp-product-form__form");
      let originalFormId = null;
      let newFormId = null;
      if (form && form.id) {
        originalFormId = form.id;
        newFormId = `${originalFormId}-${suffix}`;
        form.id = newFormId;
      }

      // 2. Repoint every `<input form="originalFormId">` inside the clone
      //    to the clone's new form id. The HTML form attribute associates
      //    inputs with a form by id regardless of DOM nesting, so without
      //    this rewrite the clone's size radios submit their value with
      //    the ORIGINAL form (in a different slide) on every Enter/submit.
      if (originalFormId && newFormId) {
        card
          .querySelectorAll(`[form="${originalFormId}"]`)
          .forEach((el) => el.setAttribute("form", newFormId));
      }

      // 3. Suffix every `<input id>` and re-point matching `<label for>`
      //    inside the same card. We collect the rename map first so the
      //    order of NodeList traversal cannot accidentally double-suffix
      //    an id we already rewrote.
      const idRenames = new Map();
      card.querySelectorAll("input[id]").forEach((input) => {
        const oldId = input.id;
        const newId = `${oldId}-${suffix}`;
        idRenames.set(oldId, newId);
        input.id = newId;
      });
      idRenames.forEach((newId, oldId) => {
        card
          .querySelectorAll(`label[for="${oldId}"]`)
          .forEach((label) => label.setAttribute("for", newId));
      });

      // 4. Suffix radio name groups so a click on the clone's radio does
      //    not deselect the original's same-name radio. Skip non-radio
      //    inputs (e.g. the form's hidden `name="id"` carries the variant
      //    id and Shopify expects the literal "id" name on submit).
      card
        .querySelectorAll("input[type='radio'][name]")
        .forEach((radio) => {
          radio.name = `${radio.name}-${suffix}`;
        });

      // Mark before logging so a re-entrant scheduleClonesReinit() that fires
      // while logs are flushing still sees the guard.
      card.dataset.scpCloneUniquified = "true";

      scpLog("clone:make-unique", {
        productId: card.dataset.productId,
        suffix,
        originalFormId,
        newFormId,
        idRenameCount: idRenames.size,
      });

      return newFormId;
    }

    /**
     * Coalesce multiple Swiper rebuild events into a single deferred
     * `initProductCards()` re-run. Combined with the per-card / per-clone
     * idempotency guards (`scpCloneUniquified`, `dataset.*Initialized`,
     * `scpQuickAddDelegationBound`), only newly-spawned clones do real
     * work on a re-run; everything else is a no-op.
     */
    scheduleClonesReinit() {
      if (this._clonesReinitScheduled) return;
      this._clonesReinitScheduled = true;
      const t = setTimeout(() => {
        this._clonesReinitScheduled = false;
        if (!this.isConnected) return;
        this.initProductCards();
      }, 80);
      this._pendingTimeouts.push(t);
    }

    /**
     * Initialize product cards
     */
    initProductCards() {
      const cards = this.querySelectorAll(".scp-feature-card");
      let duplicateCounter = 0;

      cards.forEach((card) => {
        // Check if this is a Swiper duplicate slide
        const slide = card.closest(".swiper-slide");
        const isDuplicate =
          slide && slide.classList.contains("swiper-slide-duplicate");

        if (isDuplicate) {
          // Rewrite every duplicated identifier inside the clone (form id,
          // input ids, label `for`, `<input form>` references, and radio
          // `name` groups) BEFORE any per-clone init handler runs. The
          // init* handlers cache references to inputs/overlays at bind
          // time, so the rewrite must precede them.
          duplicateCounter++;
          this.makeCloneUnique(card, `duplicate-${duplicateCounter}`);

          // Quick-add overlays are rendered per face, scoped to each face's
          // media container (.m-product-card__media for the inactive face,
          // .scp-feature-card__media for the active face). Triggers
          // (`[data-quick-add-trigger]`) are routed through
          // `attachQuickAddDelegation()`: inactive-face triggers lazy-init
          // SCPProductCard and call `instance.showVariantOverlay()`; active-
          // face triggers open `[data-scp-active-face-overlay]` directly.
          // No per-clone init needed here for the active-face quick-add flow.

          // Also initialize size selector and colors for duplicates (needed for active cards)
          this.initColorSwatches(card);
          this.initSizeSelector(card);
          this.initAddToCart(card);
          this.initWishlist(card);

          // Image carousel must work on duplicates too: with loopedSlides:12 a
          // duplicate is regularly visible, and without this its prev/next,
          // hover-swap, and tap-to-navigate would silently no-op. carouselState
          // is per-element so each clone tracks its own state independently.
          this.initCardCarousel(card);
          this.initWishlistProxy(card);

          // The inactive face renders the regular `scp-product-card` snippet,
          // which is normally wired by `initSCPProductCards()` at DOMContentLoaded.
          // That helper explicitly skips Swiper duplicates, so the nested
          // [data-scp-product-card] inside *clones* never gets a `SCPProductCard`
          // instance and its `[data-quick-add-trigger]` button silently no-ops.
          // Wire those clones here. Originals are already handled at DOM ready,
          // and the per-element `scpProductCard` guard prevents double-binding.
          if (typeof window.SCPProductCard === "function") {
            card
              .querySelectorAll("[data-scp-product-card]")
              .forEach((nested) => {
                if (!nested.scpProductCard) {
                  nested.scpProductCard = new window.SCPProductCard(nested);
                  scpLog("init:nested-product-card-on-duplicate", {
                    productId: nested.dataset.productId,
                  });
                }
              });
          }

          return;
        }

        this.initCardCarousel(card);
        this.initColorSwatches(card);
        this.initSizeSelector(card);
        this.initAddToCart(card);
        // Quick-add is handled via the per-face overlays + delegation
        // (see `attachQuickAddDelegation` below). No per-card wiring needed.
        this.initWishlist(card);
        this.initWishlistProxy(card);
      });

      // Per-card wiring above is the primary path. The delegated listener is
      // a safety net for any [data-quick-add-trigger] inside the carousel
      // whose SCPProductCard instance never bound (e.g. clones created by a
      // later Swiper rebuild, or a race between scp-product-card.js's
      // DOMContentLoaded init and Swiper's clone pass).
      this.attachQuickAddDelegation();

      // Surface form-id collisions immediately at init / rebuild time so any
      // future regression (e.g. a new clone path that bypasses
      // makeCloneUnique) is loud in both the debug build and production.
      this.auditFormIdUniqueness();
    }

    /**
     * Read-only self-audit. After every initProductCards() pass, walk every
     * `<form id="scp-product-form-...">` inside the carousel root and warn
     * loudly if any two share an id. Catches future regressions that the
     * `clone:make-unique` rewrite or its idempotency guard might miss.
     */
    auditFormIdUniqueness() {
      const ids = Array.from(
        this.querySelectorAll("form[id^='scp-product-form-']"),
      )
        .filter((el) => el instanceof HTMLFormElement)
        .map((f) => f.id);

      const seen = new Set();
      const duplicates = [];
      ids.forEach((id) => {
        if (seen.has(id)) {
          if (!duplicates.includes(id)) duplicates.push(id);
        } else {
          seen.add(id);
        }
      });

      scpLog("init:form-id-audit", { total: ids.length, duplicates });
      if (duplicates.length) {
        // Use console.warn (not gated by SCP_ATC_DEBUG) so a real collision
        // reaches a developer even on a production page without ?scp_debug=1.
        console.warn(
          "[SCP feature carousel] Duplicate <form> ids detected after clone uniquification:",
          duplicates,
        );
      }
    }

    /**
     * Carousel-root delegated click handler for `[data-quick-add-trigger]`
     * buttons. Lazily instantiates `SCPProductCard` on the closest
     * `[data-scp-product-card]` ancestor when no instance exists, then opens
     * the variant overlay. Bound only once per carousel via a dataset guard.
     */
    attachQuickAddDelegation() {
      if (this.dataset.scpQuickAddDelegationBound === "true") return;
      this.dataset.scpQuickAddDelegationBound = "true";
      scpLog("delegation:bound", {
        sectionId: this.dataset.sectionId || this.id || null,
      });

      this.addEventListener(
        "click",
        (event) => {
          const trigger =
            event.target && event.target.closest
              ? event.target.closest("[data-quick-add-trigger]")
              : null;
          if (!trigger || !this.contains(trigger)) return;

          scpLog("delegation:trigger-detected", {
            productId: trigger.dataset.productId,
            capturePhase: event.eventPhase === Event.CAPTURING_PHASE,
            defaultPrevented: event.defaultPrevented,
            triggerClasses: (trigger.className && String(trigger.className)) || "",
          });

          // Inventory of every <form id="scp-product-form-..."> currently
          // visible inside this carousel root, plus any duplicate ids. Even
          // though the quick-add trigger does not own a <form>, duplicate
          // form ids elsewhere in the same carousel can confuse Shopify's
          // form listeners — so emit the list on the same console line as
          // the failing click for easy correlation.
          const formIdsInCarousel = Array.from(
            this.querySelectorAll("form[id^='scp-product-form-']"),
          )
            // Belt-and-braces: the tag-qualified attribute selector should
            // already restrict matches to <form>, but Chrome's DevTools
            // array preview rendered earlier inventories ambiguously, so
            // the explicit instanceof check makes the diagnostic obviously
            // correct.
            .filter((el) => el instanceof HTMLFormElement)
            .map((f) => f.id);
          const seen = new Set();
          const duplicateFormIds = [];
          formIdsInCarousel.forEach((id) => {
            if (seen.has(id)) {
              if (!duplicateFormIds.includes(id)) duplicateFormIds.push(id);
            } else {
              seen.add(id);
            }
          });
          scpLog("form-id-inventory", {
            productId: trigger.dataset.productId,
            formIdsInCarousel: formIdsInCarousel,
            duplicateFormIds: duplicateFormIds,
          });

          // Resolve the .scp-feature-card root that owns this trigger.
          // Both active and inactive face triggers (when faces are
          // rendered) live inside the same root; each face renders its
          // own .scp-product-card__variant-overlay scoped to its media.
          const featureCardRoot = trigger.closest(".scp-feature-card");
          if (!featureCardRoot) {
            scpLog("delegation:no-feature-card-root", {
              productId: trigger.dataset.productId,
            });
            return;
          }

          // The trigger lives on the inactive face only when it has a
          // [data-scp-product-card] ancestor (the scp-product-card root).
          // Active-face triggers (rendered by scp-feature-product-card)
          // do NOT have that ancestor. We must NOT fall back to the
          // inactive face's nested card for active-face clicks because
          // the two faces now own separate overlays scoped to their own
          // media containers (so the user's tap-target maps 1:1 to the
          // overlay that visually appears).
          const nested = trigger.closest("[data-scp-product-card]");

          // Stop the per-instance handler (bound directly on the trigger by
          // SCPProductCard.initQuickAdd) from also firing for this same
          // click. With both bound, the overlay would open then immediately
          // close on the synthetic second invocation in some flows.
          event.preventDefault();
          event.stopPropagation();

          // Inactive-face path: lazy-init SCPProductCard on the nested
          // inactive card and use its showVariantOverlay(). All size
          // lookups, color sync, and overlay close handlers run through
          // SCPProductCard's per-card scoped queries.
          if (nested) {
            if (
              !nested.scpProductCard &&
              typeof window.SCPProductCard === "function"
            ) {
              nested.scpProductCard = new window.SCPProductCard(nested);
              scpLog("delegation:lazy-init-scp-product-card", {
                productId: nested.dataset.productId,
              });
            }

            const instance = nested.scpProductCard;
            if (instance && typeof instance.showVariantOverlay === "function") {
              // Single-variant products: scp-product-card.liquid does not
              // render the variant overlay (gated on
              // product.has_only_default_variant). Forward the click to
              // the primary [data-atc-button] so handleAddToCart fires.
              if (!instance.variantOverlay) {
                const atcButton =
                  featureCardRoot.querySelector("[data-atc-button]");
                if (atcButton && !atcButton.disabled) {
                  scpLog("delegation:single-variant-forward-to-atc", {
                    productId: nested.dataset.productId,
                  });
                  atcButton.click();
                } else {
                  scpLog("delegation:single-variant-no-atc-button", {
                    productId: nested.dataset.productId,
                  });
                }
                return;
              }

              instance.showVariantOverlay();
              scpLog("delegation:show-variant-overlay", {
                productId: nested.dataset.productId,
              });
              return;
            }

            scpLog("delegation:no-instance-or-overlay-fn", {
              productId: nested.dataset.productId,
              hasInstance: !!instance,
            });
            // Fall through to the active-face path as a last resort.
          }

          // Active-face path: trigger lives on the active face (or, when
          // the section has enable_active_card: false, in the only face
          // rendered). Open the active-face overlay rendered inside
          // .scp-feature-card__media (tagged with
          // [data-scp-active-face-overlay] so this query never hits the
          // inactive face's overlay nested under [data-scp-product-card]).
          const activeOverlay = featureCardRoot.querySelector(
            "[data-scp-active-face-overlay]",
          );

          if (!activeOverlay) {
            // Single-variant or sold-out on the active face: no overlay
            // rendered. Forward to the primary [data-atc-button] if any.
            const atcButton =
              featureCardRoot.querySelector("[data-atc-button]");
            if (atcButton && !atcButton.disabled) {
              scpLog("delegation:no-overlay-forward-to-atc", {
                productId: trigger.dataset.productId,
              });
              atcButton.click();
            } else {
              scpLog("delegation:no-overlay-no-atc-button", {
                productId: trigger.dataset.productId,
              });
            }
            return;
          }

          this.bindActiveFaceOverlayHandlers(featureCardRoot, activeOverlay);
          activeOverlay.classList.add("is-active");
          scpLog("delegation:active-overlay-open", {
            productId: trigger.dataset.productId,
          });
        },
        // Use capture so the delegated handler runs before any per-card
        // listeners further down the tree, which lets us preventDefault on a
        // navigation-style click before it bubbles out.
        true,
      );
    }

    /**
     * Wire close + size-tap handlers on the active-face variant overlay
     * (rendered inside .scp-feature-card__media by scp-feature-product-card).
     * Used when the click trigger is on the active face — either the
     * media-area button or the inline price-row button — including the
     * `enable_active_card: false` case where this is the only overlay
     * rendered for the card.
     *
     * Idempotent per overlay via a dataset guard.
     *
     * Behavior parity with SCPProductCard.initQuickAdd:
     * - `[data-overlay-close]` click removes `is-active`.
     * - Click outside the inner content (on the overlay backdrop) closes.
     * - Escape key while open closes.
     * - `[data-size-option]:not([disabled])` click forwards the variant id
     *   directly to `handleAddToCart(form, card, variantId)`. Calling the
     *   helper with the override (instead of clicking `[data-atc-button]`)
     *   bypasses the `[data-size-input]:checked` validation, which this
     *   overlay can never satisfy because scp-product-card-sizes renders
     *   `<button data-size-option>` instead of radio inputs.
     *
     * @param {HTMLElement} featureCardRoot The .scp-feature-card root.
     * @param {HTMLElement} overlay The active-face .scp-product-card__variant-overlay.
     */
    bindActiveFaceOverlayHandlers(featureCardRoot, overlay) {
      if (overlay.dataset.scpActiveOverlayBound === "true") return;
      overlay.dataset.scpActiveOverlayBound = "true";

      const closeOverlay = () => {
        overlay.classList.remove("is-active");
      };

      const closeButton = overlay.querySelector("[data-overlay-close]");
      if (closeButton) {
        closeButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeOverlay();
        });
      }

      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) {
          closeOverlay();
        }
      });

      const escapeHandler = (e) => {
        if (e.key === "Escape" && overlay.classList.contains("is-active")) {
          closeOverlay();
        }
      };
      document.addEventListener("keydown", escapeHandler);
      this.documentListeners.push({ type: "keydown", handler: escapeHandler });

      // Size option taps: find the corresponding variant id, populate the
      // form's hidden `name="id"` input, then call handleAddToCart with the
      // variant id override so the size-input validation block is skipped.
      overlay.addEventListener("click", (e) => {
        const button =
          e.target && e.target.closest
            ? e.target.closest("[data-size-option]")
            : null;
        if (!button) return;
        if (button.disabled) return;

        e.preventDefault();
        e.stopPropagation();

        const variantId = button.dataset.variantId;
        const form =
          featureCardRoot.querySelector(".scp-product-form__form");
        const variantInput = form && form.querySelector("[data-variant-id]");

        scpLog("active-overlay:size-tap", {
          productId: featureCardRoot.dataset.productId,
          variantId: variantId || null,
          hasForm: !!form,
          hasVariantInput: !!variantInput,
        });

        if (!variantId || !form || !variantInput) {
          return;
        }

        variantInput.setAttribute("value", variantId);
        variantInput.value = variantId;
        variantInput.disabled = false;

        closeOverlay();
        this.handleAddToCart(form, featureCardRoot, variantId);
      });
    }

    /**
     * Wire the inactive-face wishlist proxy button to forward clicks to the
     * active face's real wishlist control. Avoids duplicating wishlist
     * widgets per card (which would conflict on growave / form IDs).
     */
    initWishlistProxy(card) {
      if (!card || card.dataset.scpWishlistProxyBound === "true") return;
      const proxies = card.querySelectorAll("[data-scp-wishlist-proxy]");
      if (!proxies.length) return;

      card.dataset.scpWishlistProxyBound = "true";

      proxies.forEach((proxy) => {
        proxy.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const target =
            card.querySelector(
              ".scp-feature-card__face--active button[data-scp-wishlist]"
            ) ||
            card.querySelector(
              ".scp-feature-card__face--active .m-wishlist-button"
            ) ||
            card.querySelector(
              ".scp-feature-card__face--active [data-scp-wishlist]"
            );
          if (target && typeof target.click === "function") {
            target.click();
          }
        });
      });
    }

    /**
     * Initialize wishlist button inside a feature card (ATC row button with
     * class .scp-feature-card__wishlist-button and attribute data-scp-wishlist).
     * Mirrors the toggle pattern used by MinimogTheme.Wishlist / scp-buy-buttons.
     */
    initWishlist(card) {
      const buttons = card.querySelectorAll(
        "button.scp-feature-card__wishlist-button[data-scp-wishlist]"
      );
      if (!buttons.length) return;

      const storageKey = "m-wishlist-products";

      const readWishlist = () => {
        try {
          return JSON.parse(localStorage.getItem(storageKey) || "[]") || [];
        } catch (e) {
          return [];
        }
      };

      const writeWishlist = (list) => {
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify(Array.from(new Set(list)))
          );
        } catch (e) {
          // Ignore storage failures (private mode, quota, etc.)
        }
      };

      const applyActiveState = (btn, active) => {
        if (!btn) return;
        if (active) {
          btn.classList.add("added-to-wishlist");
          btn.setAttribute(
            "aria-label",
            btn.dataset.labelRemove ||
              window.MinimogStrings?.removeFromWishlist ||
              "Remove from wishlist"
          );
        } else {
          btn.classList.remove("added-to-wishlist");
          btn.setAttribute(
            "aria-label",
            btn.dataset.labelAdd ||
              window.MinimogStrings?.addToWishlist ||
              "Add to wishlist"
          );
        }
      };

      const syncAllButtonsFor = (handle, active) => {
        if (!handle) return;
        document
          .querySelectorAll(`[data-product-handle="${handle}"]`)
          .forEach((el) => {
            if (
              el.classList.contains("scp-feature-card__wishlist-button") ||
              el.classList.contains("m-wishlist-button") ||
              el.classList.contains("scp-buy-buttons__wishlist")
            ) {
              applyActiveState(el, active);
              if (el.classList.contains("m-wishlist-button")) {
                // Keep legacy class for any CSS still keyed off it
                if (active) el.classList.add("is-active");
                else el.classList.remove("is-active");
              }
            }
          });
      };

      buttons.forEach((btn) => {
        if (btn.dataset.scpWishlistBound === "true") return;
        btn.dataset.scpWishlistBound = "true";

        const handle = btn.dataset.productHandle;

        // Initial state from storage
        if (handle && readWishlist().indexOf(handle) !== -1) {
          applyActiveState(btn, true);
        }

        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          const productHandle = btn.dataset.productHandle;
          if (!productHandle) return;

          const isActive = btn.classList.contains("added-to-wishlist");
          const newState = !isActive;

          if (window.MinimogTheme && window.MinimogTheme.Wishlist) {
            const wl = window.MinimogTheme.Wishlist;
            if (newState) {
              wl.addToWishlist(productHandle);
            } else {
              wl.removeFromWishlist(productHandle);
            }
            applyActiveState(btn, newState);
            if (typeof wl.updateWishlistCount === "function") {
              wl.updateWishlistCount();
            }
          } else {
            // Fallback: direct localStorage manipulation
            let list = readWishlist();
            if (newState) {
              list.push(productHandle);
            } else {
              list = list.filter((h) => h !== productHandle);
            }
            writeWishlist(list);
            applyActiveState(btn, newState);
          }

          syncAllButtonsFor(productHandle, newState);
        });
      });
    }

    /**
     * Initialize image carousel for a product card
     */
    initCardCarousel(card) {
      // When enable_active_card is on, the card has two faces and both render
      // `[data-scp-carousel]` (the inactive face via the scp-product-card snippet).
      // Scope to the active face when present so the active card's visible
      // prev/next arrows are wired up; the inactive face's carousel is owned
      // by scp-product-card.js. Falls back to the card itself when
      // enable_active_card is off.
      const scope = card.querySelector(".scp-feature-card__face--active") || card;

      const carousel = scope.querySelector("[data-scp-carousel]");
      if (!carousel) return;

      const slides = scope.querySelectorAll("[data-carousel-slide]");
      const prevButton = scope.querySelector("[data-carousel-prev]");
      const nextButton = scope.querySelector("[data-carousel-next]");
      const carouselTrack = scope.querySelector("[data-carousel-track]");

      if (slides.length <= 1) return;

      // Store carousel state on the card element
      card.carouselState = {
        currentSlide: 0,
        totalSlides: slides.length,
        slides: slides,
        isHoverActive: false,
        hoverTimeout: null,
        touchStartX: 0,
        touchEndX: 0,
        touchStartY: 0,
        mouseDownX: 0,
        mouseDownY: 0,
        // Tap target captured on touchstart so we can skip navigate-to-product
        // when the user tapped a button/anchor/swatch inside the carousel track.
        touchTarget: null,
        // Flag set on touchend, cleared after ~400ms, used to suppress the
        // synthetic mouseup navigate path on hybrid touch laptops.
        justTouched: false,
        justTouchedTimeout: null,
      };

      // Navigation buttons
      if (prevButton) {
        prevButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.prevSlide(card);
        });
      }

      if (nextButton) {
        nextButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.nextSlide(card);
        });
      }

      // Hover to show second image (desktop only)
      if (!this.isMobile && slides.length > 1) {
        const mediaElement = scope.querySelector(".scp-feature-card__media");

        if (mediaElement) {
          mediaElement.addEventListener("mouseenter", () => {
            // Only auto-change if we're on the first slide
            if (card.carouselState.currentSlide === 0) {
              card.carouselState.hoverTimeout = setTimeout(() => {
                this.goToSlide(card, 1);
                card.carouselState.isHoverActive = true;
              }, 200); // Small delay to prevent accidental triggers
            }
          });

          mediaElement.addEventListener("mouseleave", () => {
            // Clear timeout if mouse leaves before delay
            if (card.carouselState.hoverTimeout) {
              clearTimeout(card.carouselState.hoverTimeout);
              card.carouselState.hoverTimeout = null;
            }

            // Return to first slide if hover was active and no manual navigation occurred
            if (
              card.carouselState.isHoverActive &&
              card.carouselState.currentSlide === 1
            ) {
              this.goToSlide(card, 0);
            }
            card.carouselState.isHoverActive = false;
          });
        }
      }

      // Touch/swipe support
      if (carouselTrack) {
        // Stop the parent Swiper from registering this gesture on touchstart.
        // touchmove-only stopPropagation is too late: Swiper's wrapper-level
        // handler already locked the gesture on touchstart and would slide the
        // row even though the user is interacting with the inner image carousel.
        carouselTrack.addEventListener(
          "touchstart",
          (e) => {
            if (!card.carouselState) return;
            e.stopPropagation();
            card.carouselState.touchStartX = e.changedTouches[0].screenX;
            card.carouselState.touchStartY = e.changedTouches[0].screenY;
            // Capture the actual target so taps on prev/next/wishlist/swatch
            // skip the navigate-to-product short-circuit in touchend.
            card.carouselState.touchTarget = e.target.closest(
              "button, a, [data-color-swatch], [data-size-input], label",
            );
          },
          { passive: true },
        );

        // Intercept horizontal swipes to prevent parent Swiper from capturing them
        carouselTrack.addEventListener(
          "touchmove",
          (e) => {
            if (!card.carouselState) return;

            const currentX = e.changedTouches[0].screenX;
            const currentY = e.changedTouches[0].screenY;
            const dx = Math.abs(currentX - card.carouselState.touchStartX);
            const dy = Math.abs(currentY - card.carouselState.touchStartY);

            // If horizontal movement is dominant and exceeds threshold,
            // prevent event from reaching parent Swiper
            if (dx > dy && dx > 10) {
              e.stopPropagation();
            }
          },
          { passive: true },
        );

        carouselTrack.addEventListener(
          "touchend",
          (e) => {
            if (!card.carouselState) return;

            card.carouselState.touchEndX = e.changedTouches[0].screenX;
            const touchEndY = e.changedTouches[0].screenY;
            const dx = Math.abs(
              card.carouselState.touchEndX - card.carouselState.touchStartX,
            );
            const dy = Math.abs(touchEndY - card.carouselState.touchStartY);

            // Mark the just-touched window so the synthetic mouseup that follows
            // on hybrid devices does not also try to navigate to the product.
            card.carouselState.justTouched = true;
            if (card.carouselState.justTouchedTimeout) {
              clearTimeout(card.carouselState.justTouchedTimeout);
            }
            const touchFlagTimeout = setTimeout(() => {
              if (card.carouselState) {
                card.carouselState.justTouched = false;
                card.carouselState.justTouchedTimeout = null;
              }
            }, 400);
            card.carouselState.justTouchedTimeout = touchFlagTimeout;
            this._pendingTimeouts.push(touchFlagTimeout);

            // If the user tapped an interactive element inside the track
            // (prev/next button, wishlist, swatch), let its own click fire.
            const tappedInteractive = card.carouselState.touchTarget;
            card.carouselState.touchTarget = null;

            if (tappedInteractive) {
              return;
            }

            // If finger barely moved, treat as tap -> navigate to product
            if (dx < 10 && dy < 10) {
              const productUrl = card.querySelector(
                ".scp-feature-card__link",
              )?.href;
              if (productUrl) {
                window.location.href = productUrl;
                return;
              }
            }

            // Otherwise handle as swipe
            this.handleSwipe(card);
          },
          { passive: true },
        );

        // Mouse click-through support (desktop)
        carouselTrack.addEventListener("mousedown", (e) => {
          if (!card.carouselState) return;
          card.carouselState.mouseDownX = e.clientX;
          card.carouselState.mouseDownY = e.clientY;
          card.carouselState.mouseDownTarget = e.target.closest(
            "button, a, [data-color-swatch], [data-size-input], label",
          );
        });

        carouselTrack.addEventListener("mouseup", (e) => {
          if (!card.carouselState) return;

          // Suppress synthetic mouseup that follows touchend on hybrid devices.
          if (card.carouselState.justTouched) {
            return;
          }

          // If the user clicked an interactive element, let its handler run.
          const clickedInteractive = card.carouselState.mouseDownTarget;
          card.carouselState.mouseDownTarget = null;
          if (clickedInteractive) {
            return;
          }

          const dx = Math.abs(e.clientX - card.carouselState.mouseDownX);
          const dy = Math.abs(e.clientY - card.carouselState.mouseDownY);

          // If mouse barely moved, treat as click -> navigate to product
          if (dx < 5 && dy < 5) {
            const productUrl = card.querySelector(
              ".scp-feature-card__link",
            )?.href;
            if (productUrl) {
              window.location.href = productUrl;
            }
          }
        });
      }
    }

    /**
     * Navigate to previous slide
     */
    prevSlide(card) {
      if (!card.carouselState) return;

      // Disable hover auto-return when manually navigating
      card.carouselState.isHoverActive = false;

      if (card.carouselState.currentSlide > 0) {
        this.goToSlide(card, card.carouselState.currentSlide - 1);
      } else {
        // Loop to last slide
        this.goToSlide(card, card.carouselState.totalSlides - 1);
      }
    }

    /**
     * Navigate to next slide
     */
    nextSlide(card) {
      if (!card.carouselState) return;

      // Disable hover auto-return when manually navigating
      card.carouselState.isHoverActive = false;

      if (
        card.carouselState.currentSlide <
        card.carouselState.totalSlides - 1
      ) {
        this.goToSlide(card, card.carouselState.currentSlide + 1);
      } else {
        // Loop to first slide
        this.goToSlide(card, 0);
      }
    }

    /**
     * Go to specific slide
     */
    goToSlide(card, index) {
      if (!card.carouselState) return;
      if (index < 0 || index >= card.carouselState.totalSlides) return;

      const slides = card.carouselState.slides;

      // Remove active class from current slide
      slides[card.carouselState.currentSlide].classList.remove("is-active");

      // Add active class to new slide
      slides[index].classList.add("is-active", "is-transitioning");

      // Remove transitioning class after animation
      const transitionTimeout = setTimeout(() => {
        slides[index].classList.remove("is-transitioning");
      }, 300);
      this._pendingTimeouts.push(transitionTimeout);

      card.carouselState.currentSlide = index;
    }

    /**
     * Handle touch swipe
     */
    handleSwipe(card) {
      if (!card.carouselState) return;

      const swipeThreshold = 50;
      const diff =
        card.carouselState.touchStartX - card.carouselState.touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next slide
          this.nextSlide(card);
        } else {
          // Swipe right - prev slide
          this.prevSlide(card);
        }
      }
    }

    /**
     * Initialize color swatches
     */
    initColorSwatches(card) {
      const colorSwatches = card.querySelectorAll("[data-color-swatch]");

      if (!colorSwatches.length) return;

      // Prevent duplicate initialization
      if (card.dataset.colorSwatchesInitialized === "true") {
        return;
      }
      card.dataset.colorSwatchesInitialized = "true";

      // Use event delegation on the card for more robust click handling
      card.addEventListener("click", (e) => {
        const swatch = e.target.closest("[data-color-swatch]");
        if (swatch) {
          e.preventDefault();
          e.stopPropagation();
          this.handleColorChange(card, swatch);
        }
      });

      // Initialize size availability based on pre-selected first color
      const selectedColor = card.querySelector(
        '[data-color-swatch][data-selected="true"]',
      );
      if (selectedColor) {
        const optionPosition =
          parseInt(selectedColor.dataset.optionPosition) || 1;
        const optionValue = selectedColor.dataset.value;

        // Get product variants
        const variants = this.getProductData(card);
        if (variants) {
          // Update size availability for the pre-selected color
          this.updateSizeAvailability(
            card,
            optionPosition,
            optionValue,
            variants,
          );

          // Find first available variant with this color (no size selected yet)
          const colorVariant =
            variants.find((v) => {
              return (
                v.options[optionPosition - 1] === optionValue && v.available
              );
            }) ||
            variants.find((v) => {
              return v.options[optionPosition - 1] === optionValue;
            });

          // Update the card with this variant (updates SKU, price, etc.)
          if (colorVariant) {
            this.updateCardWithVariant(card, colorVariant);
          }
        }
      }
    }

    /**
     * Handle color swatch change
     */
    handleColorChange(card, swatch) {
      // Check if this is a duplicate slide
      const slide = card.closest(".swiper-slide");
      const isDuplicate = slide?.classList.contains("swiper-slide-duplicate");

      const optionPosition = parseInt(swatch.dataset.optionPosition) || 1;
      const optionValue = swatch.dataset.value;
      const productId = card.dataset.productId;

      // Find the original (non-duplicate) card
      let targetCard = card;
      if (isDuplicate) {
        const allCards = this.querySelectorAll(
          `[data-product-id="${productId}"]`,
        );
        for (const c of allCards) {
          const s = c.closest(".swiper-slide");
          if (!s?.classList.contains("swiper-slide-duplicate")) {
            targetCard = c;
            break;
          }
        }
      }

      // Sync color swatch visual state across ALL cards with this product ID
      const allProductCards = this.querySelectorAll(
        `[data-product-id="${productId}"]`,
      );
      allProductCards.forEach((productCard) => {
        const swatches = productCard.querySelectorAll("[data-color-swatch]");
        swatches.forEach((s) => {
          if (s.dataset.value === optionValue) {
            s.setAttribute("data-selected", "true");
          } else {
            s.removeAttribute("data-selected");
          }
        });
      });

      // Get product variants from TARGET card
      const variants = this.getProductData(targetCard);
      if (!variants) {
        return;
      }

      // Find first available variant with this color
      const colorVariant =
        variants.find((v) => {
          return v.options[optionPosition - 1] === optionValue && v.available;
        }) ||
        variants.find((v) => {
          return v.options[optionPosition - 1] === optionValue;
        });

      // Update image if variant has one
      if (colorVariant && colorVariant.featured_image) {
        this.updateImage(targetCard, colorVariant.featured_image);
      }

      // Reset size selection when color changes - sync across ALL cards
      allProductCards.forEach((productCard) => {
        const sizeInputs = productCard.querySelectorAll("[data-size-input]");
        sizeInputs.forEach((input) => (input.checked = false));
      });

      // Update size availability based on selected color on TARGET card
      this.updateSizeAvailability(
        targetCard,
        optionPosition,
        optionValue,
        variants,
      );

      // Update variant with color selection only (no size selected yet)
      if (colorVariant) {
        this.updateCardWithVariant(targetCard, colorVariant);
      }
    }

    /**
     * Update size availability based on selected color
     */
    updateSizeAvailability(card, colorPosition, colorValue, variants) {
      const sizeInputs = card.querySelectorAll("[data-size-input]");
      if (!sizeInputs.length) return;

      sizeInputs.forEach((input) => {
        const sizePosition = parseInt(input.dataset.optionPosition) || 2;
        const sizeValue = input.dataset.optionValue;
        const sizeLabel = input.nextElementSibling; // The label element

        // Find if there's an available variant with this color + size combination
        const matchingVariant = variants.find((v) => {
          return (
            v.options[colorPosition - 1] === colorValue &&
            v.options[sizePosition - 1] === sizeValue &&
            v.available
          );
        });

        // Update input and label state
        if (matchingVariant) {
          input.disabled = false;
          if (sizeLabel) {
            sizeLabel.classList.remove("disabled");
            sizeLabel.removeAttribute("data-soldout");
          }
        } else {
          input.disabled = true;
          if (sizeLabel) {
            sizeLabel.classList.add("disabled");
            sizeLabel.setAttribute("data-soldout", "true");
          }
        }
      });
    }

    /**
     * Initialize size selector
     */
    initSizeSelector(card) {
      const sizeInputs = card.querySelectorAll("[data-size-input]");

      if (!sizeInputs.length) {
        return;
      }

      // Prevent duplicate initialization
      if (card.dataset.sizeSelectorInitialized === "true") {
        return;
      }
      card.dataset.sizeSelectorInitialized = "true";

      // Use event delegation on the card for change events
      // The change event target IS the input element, so check with matches()
      card.addEventListener("change", (e) => {
        if (e.target.matches("[data-size-input]")) {
          this.handleSizeChange(card, e.target);
        }
      });

      // Also listen for click on size labels (more reliable for styled radio buttons)
      card.addEventListener("click", (e) => {
        const sizeLabel = e.target.closest(".scp-feature-card__size-label");
        if (sizeLabel) {
          // Find the associated input via the label's "for" attribute
          const inputId = sizeLabel.getAttribute("for");
          if (inputId) {
            const input = card.querySelector(`#${CSS.escape(inputId)}`);
            if (input && !input.disabled) {
              // Manually check the input and trigger change handling
              input.checked = true;
              this.handleSizeChange(card, input);
            }
          }
        }
      });
    }

    /**
     * Handle size change
     */
    handleSizeChange(card, input) {
      // Check if this is a duplicate slide
      const slide = card.closest(".swiper-slide");
      const isDuplicate = slide?.classList.contains("swiper-slide-duplicate");

      const sizePosition = parseInt(input.dataset.optionPosition) || 2;
      const sizeValue = input.dataset.optionValue;
      const productId = card.dataset.productId;

      // Find the original (non-duplicate) card
      let targetCard = card;
      if (isDuplicate) {
        const allCards = this.querySelectorAll(
          `[data-product-id="${productId}"]`,
        );
        for (const c of allCards) {
          const s = c.closest(".swiper-slide");
          if (!s?.classList.contains("swiper-slide-duplicate")) {
            targetCard = c;
            break;
          }
        }
      }

      // Get product variants from TARGET card
      const variants = this.getProductData(targetCard);
      if (!variants) {
        return;
      }

      // Get selected color from TARGET card (may not exist for size-only products)
      const selectedColor = targetCard.querySelector(
        '[data-color-swatch][data-selected="true"]',
      );

      let matchingVariant;

      if (selectedColor) {
        // Product has both color and size options
        const colorPosition =
          parseInt(selectedColor.dataset.optionPosition) || 1;
        const colorValue = selectedColor.dataset.value;

        // Find exact matching variant with both color and size
        matchingVariant = variants.find((v) => {
          return (
            v.options[colorPosition - 1] === colorValue &&
            v.options[sizePosition - 1] === sizeValue
          );
        });
      } else {
        // Product has only size option (no color)
        matchingVariant = variants.find((v) => {
          return v.options[sizePosition - 1] === sizeValue;
        });
      }

      if (matchingVariant) {
        // Sync size selection visual state across ALL cards with this product ID
        const allProductCards = this.querySelectorAll(
          `[data-product-id="${productId}"]`,
        );
        allProductCards.forEach((productCard) => {
          // First, remove selected class from all size options on this card
          const allSizeOptions = productCard.querySelectorAll(
            ".scp-feature-card__size-option",
          );
          allSizeOptions.forEach((option) => {
            option.classList.remove("is-selected");
            const inp = option.querySelector("[data-size-input]");
            if (inp) inp.checked = false;
          });

          // Then select the matching size option
          const sizeInput = productCard.querySelector(
            `[data-size-input][data-option-value="${sizeValue}"]`,
          );
          if (sizeInput) {
            sizeInput.checked = true;
            // Add visual class to parent option wrapper for reliable CSS styling
            const sizeOption = sizeInput.closest(
              ".scp-feature-card__size-option",
            );
            if (sizeOption) {
              sizeOption.classList.add("is-selected");
            }
          }

          // Update variant info and availability on ALL cards (not just targetCard)
          this.updateCardWithVariant(productCard, matchingVariant);
        });
      }
    }

    /**
     * Update card with new variant data
     */
    updateCardWithVariant(card, variant) {
      // Update hidden input
      const variantInput = card.querySelector("[data-variant-id]");
      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.disabled = false; // Enable the input so it gets submitted
      }

      // Update SKU
      const skuElement = card.querySelector("[data-variant-sku]");
      if (skuElement) {
        const skuLabel = skuElement.textContent.split(":")[0];
        skuElement.textContent = `${skuLabel}: ${variant.sku || "N/A"}`;
      }

      // Update price
      this.updatePrice(card, variant);

      // Update availability
      this.updateAvailability(card, variant);

      // Update image if available
      if (variant.featured_image) {
        this.updateImage(card, variant.featured_image);
      }
    }

    /**
     * Update price display
     */
    updatePrice(card, variant) {
      const priceWrapper = card.querySelector("[data-price-wrapper]");
      if (!priceWrapper) return;

      const priceElement = priceWrapper.querySelector(".m-price-item--regular");
      const salePriceElement = priceWrapper.querySelector(
        ".m-price-item--sale",
      );
      const compareElement = priceWrapper.querySelector(
        ".m-price-item--regular s, .m-price__sale s",
      );

      if (priceElement) {
        priceElement.textContent = this.formatMoney(variant.price);
      }

      if (salePriceElement) {
        salePriceElement.textContent = this.formatMoney(variant.price);
      }

      if (compareElement && variant.compare_at_price > variant.price) {
        compareElement.textContent = this.formatMoney(variant.compare_at_price);
      }
    }

    /**
     * Update availability (add to cart button state)
     */
    updateAvailability(card, variant) {
      const atcButton = card.querySelector("[data-atc-button]");
      const atcText = card.querySelector(".scp-feature-card__atc-text");

      if (!atcButton) return;

      if (variant.available) {
        atcButton.disabled = false;
        atcButton.classList.remove("soldout");
        atcButton.removeAttribute("data-soldout");
        if (atcText) {
          atcText.textContent =
            atcButton.dataset.atcText ||
            window.MinimogStrings?.addToCart ||
            "Add to cart";
        }
      } else {
        atcButton.disabled = true;
        atcButton.classList.add("soldout");
        atcButton.setAttribute("data-soldout", "true");
        if (atcText) {
          atcText.textContent =
            window.MinimogStrings?.soldOut || "Sold out";
        }
      }
    }

    /**
     * Update product image - works with carousel structure
     */
    updateImage(card, image) {
      // Find the currently active carousel slide
      const activeSlide = card.querySelector(
        ".scp-feature-card__carousel-slide.is-active",
      );
      if (!activeSlide) return;

      // Find the image within the active slide
      const slideImage = activeSlide.querySelector("img");
      if (slideImage && image.src) {
        slideImage.src = image.src;
        slideImage.srcset = image.src;
        slideImage.alt = image.alt || "";
      }
    }

    /**
     * Initialize add to cart functionality
     * Attach our own submit listener to intercept and handle with spinner
     */
    initAddToCart(card) {
      const form = card.querySelector(".scp-product-form__form");

      if (!form) return;

      // Check if already initialized
      if (form.dataset.scpAtcInitialized === "true") return;
      form.dataset.scpAtcInitialized = "true";

      const slide = card.closest(".swiper-slide");
      scpLog("init:active-form", {
        productId: card.dataset.productId,
        formId: form.id,
        isDuplicate: !!slide?.classList.contains("swiper-slide-duplicate"),
      });

      // Attach submit listener with high priority (capture phase)
      form.addEventListener(
        "submit",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          this.handleAddToCart(form, card);
        },
        true,
      ); // Use capture phase to intercept before theme's listener
    }

    /**
     * Handle add to cart
     * @param {HTMLFormElement} form - The product form
     * @param {HTMLElement} card - The product card
     * @param {string} [variantIdOverride] - Optional variant ID to use instead of reading from form
     */
    async handleAddToCart(form, card, variantIdOverride = null) {
      const submitButton = form.querySelector("[data-atc-button]");
      let errorContainer = card.querySelector("[data-error-message]");
      const productId = card.dataset.productId;
      const slideEl = card.closest(".swiper-slide");
      const isDuplicate = !!slideEl?.classList.contains("swiper-slide-duplicate");

      scpLog("submit:start", {
        productId,
        formId: form?.id,
        hasOverride: !!variantIdOverride,
        isDuplicate,
        alreadySubmitting: submitButton?.dataset.submitting === "true",
      });

      // Guard against missing submit button
      if (!submitButton) {
        console.warn("[SCP Feature Carousel] Submit button not found in form");
        return;
      }

      // Prevent double submission
      if (submitButton.dataset.submitting === "true") return;
      submitButton.dataset.submitting = "true";

      // Clear previous errors
      if (errorContainer) {
        errorContainer.textContent = "";
      }

      // Check if this is a multi-variant product that requires size selection
      // Skip this check if variantIdOverride is provided (e.g., from Quick Add overlay)
      // Important: Look across ALL cards with this product ID since Swiper duplicates may have the checked state
      const sizeInputs = card.querySelectorAll("[data-size-input]");
      let sizeFoundOnSelf = false;
      let sizeSyncedFromCard = null;
      let resolvedSize = null;
      if (sizeInputs.length > 0 && !variantIdOverride) {
        // First check this card
        let selectedSize = card.querySelector("[data-size-input]:checked");
        sizeFoundOnSelf = !!selectedSize;

        // If not found on this card, check all cards with this product ID (Swiper duplicates)
        if (!selectedSize && productId) {
          const allProductCards = this.querySelectorAll(
            `[data-product-id="${productId}"]`,
          );
          for (const productCard of allProductCards) {
            selectedSize = productCard.querySelector(
              "[data-size-input]:checked",
            );
            if (selectedSize) {
              sizeSyncedFromCard = productCard.dataset.productId || true;
              // Found the checked size on another card - sync state to this card
              const sizeValue = selectedSize.dataset.optionValue;
              const thisCardInput = card.querySelector(
                `[data-size-input][data-option-value="${sizeValue}"]`,
              );
              if (thisCardInput) {
                thisCardInput.checked = true;
              }
              // Also update the variant ID on this card's form
              const variantInput = form.querySelector("[data-variant-id]");
              const sourceVariantInput =
                productCard.querySelector("[data-variant-id]");
              if (variantInput && sourceVariantInput) {
                variantInput.value = sourceVariantInput.value;
              }
              break;
            }
          }
        }

        resolvedSize = selectedSize;

        scpLog("submit:size", {
          required: true,
          foundOnSelf: sizeFoundOnSelf,
          syncedFromAnotherCard: !!sizeSyncedFromCard,
          finalSize: selectedSize?.dataset.optionValue || null,
        });

        if (!selectedSize) {
          const selectSizeMsg =
            window.MinimogStrings?.pleaseSelectSize || "Please select a size";
          this.showError(errorContainer, selectSizeMsg);
          submitButton.dataset.submitting = "false";
          return;
        }
      } else {
        scpLog("submit:size", {
          required: false,
          reason: variantIdOverride ? "override-provided" : "no-size-inputs",
        });
      }

      // Enable the variant input first (it starts disabled in Liquid)
      const variantInput = form.querySelector("[data-variant-id]");
      if (variantInput) {
        variantInput.disabled = false;
      }

      // Use override if provided, otherwise read from form
      let variantId = variantIdOverride;
      let variantSource = "override";

      if (!variantId) {
        // Get form data (now includes the enabled input)
        const formData = new FormData(form);
        variantId = formData.get("id");
        if (variantId) {
          variantSource = "form-data";
        }

        // Fallback: check the hidden input directly
        if (!variantId && variantInput) {
          variantId = variantInput.value;
          variantSource = "input-value";
        }
      }

      scpLog("submit:variant", {
        variantId: variantId || null,
        source: variantId ? variantSource : "none",
        inputDisabled: variantInput ? variantInput.disabled : null,
      });

      if (!variantId) {
        const selectVariantMsg =
          window.MinimogStrings?.pleaseSelectVariant || "Please select a variant";
        this.showError(errorContainer, selectVariantMsg);
        submitButton.dataset.submitting = "false";
        return;
      }

      // Show loading state (theme's m-spinner-icon will appear automatically)
      submitButton.classList.add("is-loading");
      submitButton.disabled = true;

      try {
        // Gather sections for cart drawer refresh (native Minimog pattern)
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:grouped-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          }),
        );

        scpLog("submit:sections", {
          count: sectionsToBundle.length,
          sections: sectionsToBundle,
        });

        const cartAddUrl =
          window.MinimogSettings?.routes?.cart_add_url || "/cart/add.js";

        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", 1);
        formData.append("sections", sectionsToBundle.join(","));
        formData.append("sections_url", window.location.pathname);

        scpLog("submit:request", { url: cartAddUrl, variantId });

        const response = await fetch(cartAddUrl, {
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          body: formData,
        });

        const contentType = response.headers.get("content-type");

        scpLog("submit:response", {
          status: response.status,
          ok: response.ok,
          contentType,
        });

        if (!contentType || !contentType.includes("application/json")) {
          // Capture body so we can see *what* came back (Shopify password
          // page, HTML 5xx, gateway HTML, etc.) when debugging.
          let bodySnippet = "";
          try {
            const bodyText = await response.text();
            bodySnippet = (bodyText || "").slice(0, 200);
          } catch (_e) {
            bodySnippet = "<failed to read body>";
          }
          scpLog("submit:non-json-body", {
            status: response.status,
            contentType,
            bodySnippet,
          });
          console.error(
            "[SCP Feature Carousel] Non-JSON response. Status:",
            response.status,
            "Content-Type:",
            contentType,
          );
          const fallbackInvalidResponse =
            window.MinimogStrings?.cartInvalidResponse ||
            "Server returned an invalid response. Please refresh and try again.";
          throw new Error(fallbackInvalidResponse);
        }

        const result = await response.json();

        // Handle error responses (422 Unprocessable Content, etc.)
        if (!response.ok || result.status) {
          scpLog("submit:error-payload", {
            status: result.status,
            description: result.description,
            message: result.message,
          });
          // Shopify returns error info in description or message
          const fallbackCartError =
            window.MinimogStrings?.cartError || "Unable to add to cart";
          const errorMessage =
            result.description || result.message || fallbackCartError;
          throw new Error(errorMessage);
        }

        scpLog("submit:success", {
          variantId,
          hasSections: !!result.sections,
          sectionKeys: result.sections ? Object.keys(result.sections) : [],
        });

        // Only emit cart update event on SUCCESS
        if (
          window.MinimogEvents &&
          window.MinimogTheme?.pubSubEvents?.cartUpdate
        ) {
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
            cart: result,
            productVariantId: variantId,
          });
        }

        // Open cart drawer with success notification
        const cartDrawer = document.querySelector("m-cart-drawer");
        scpLog("submit:cart-drawer", {
          found: !!cartDrawer,
          hasOpen: !!cartDrawer?.open,
        });
        if (cartDrawer) {
          if (
            window.MinimogTheme?.Notification &&
            window.MinimogStrings?.itemAdded
          ) {
            MinimogTheme.Notification.show({
              target: cartDrawer.querySelector("m-cart-items"),
              method: "prepend",
              type: "success",
              message: MinimogStrings.itemAdded,
              delay: 400,
            });
          }
          cartDrawer.open();
        }

        document.dispatchEvent(
          new CustomEvent("product-ajax:added", {
            detail: { product: result },
          }),
        );
      } catch (error) {
        console.error("[SCP Feature Carousel] Add to cart error:", error);
        const fallbackErrorMsg =
          window.MinimogStrings?.cartError ||
          "Unable to add to cart. Please try again.";
        this.showError(errorContainer, error.message || fallbackErrorMsg);
      } finally {
        // Remove loading state
        submitButton.classList.remove("is-loading");
        submitButton.disabled = false;
        submitButton.dataset.submitting = "false";
      }
    }

    /**
     * Show error message
     */
    showError(container, message) {
      console.error("[SCP Feature Carousel] Error feedback:", message);

      // Use sticky notification pattern for better visibility
      if (window.MinimogTheme?.Notification) {
        MinimogTheme.Notification.show({
          target: document.body,
          method: "prepend",
          type: "warning",
          message: message,
          sticky: true,
          delay: 0,
        });
      } else if (container) {
        container.textContent = message;
        const clearErrorTimeout = setTimeout(() => {
          container.textContent = "";
        }, 5000);
        this._pendingTimeouts.push(clearErrorTimeout);
      }
    }

    /**
     * Format money using theme's global formatMoney function
     */
    formatMoney(cents) {
      if (
        typeof formatMoney === "function" &&
        window.MinimogSettings?.money_format
      ) {
        return formatMoney(cents, window.MinimogSettings.money_format);
      }
      // Fallback if global function not available
      const amount = cents / 100;
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: window.Shopify?.currency?.active || "EUR",
      }).format(amount);
    }

    /**
     * Open cart drawer (same as other product components)
     */
    openCartDrawer() {
      const cartDrawer = document.querySelector("m-cart-drawer");
      if (cartDrawer && typeof cartDrawer.open === "function") {
        cartDrawer.open();
      }
    }

    /**
     * Get product variant data from card
     */
    getProductData(card) {
      // Prefer the carousel's OWN script (direct child of .scp-feature-card,
      // contains `product.variants | json`). The inactive face renders the
      // regular `scp-product-card` snippet, which embeds its own
      // `<script data-product-json>` containing the full product object —
      // matching that one first would make `.find` blow up downstream.
      const scoped = card.querySelector(":scope > script[data-product-json]");
      const productJson = scoped || card.querySelector("[data-product-json]");
      if (!productJson) return null;

      try {
        const parsed = JSON.parse(productJson.textContent);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && Array.isArray(parsed.variants)) return parsed.variants;
        scpLog("getProductData:unexpected-shape", {
          productId: card.dataset.productId,
          keys: parsed && typeof parsed === "object"
            ? Object.keys(parsed).slice(0, 5)
            : typeof parsed,
        });
        return null;
      } catch (error) {
        scpLog("getProductData:parse-error", {
          productId: card.dataset.productId,
          error: error.message,
        });
        return null;
      }
    }

    /**
     * Cleanup on disconnect
     */
    disconnectedCallback() {
      // Explicit clear before the bulk flush so a rapid disconnect during
      // the parallel translate animation does not leave dangling timers.
      if (this._scpAlignTimer) {
        clearTimeout(this._scpAlignTimer);
        this._scpAlignTimer = null;
      }

      // Clear all pending timeouts to prevent orphaned callbacks
      if (this._pendingTimeouts && this._pendingTimeouts.length > 0) {
        this._pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
        this._pendingTimeouts = [];
      }

      this.detachWheelSlideScroll();

      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      // Remove resize handler
      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
        this.resizeHandler = null;
      }

      // Remove all document-level event listeners
      if (this.documentListeners) {
        this.documentListeners.forEach(({ type, handler }) => {
          document.removeEventListener(type, handler);
        });
        this.documentListeners = [];
      }
    }
  }

  customElements.define("scp-feature-carousel", SCPFeatureCarousel);
}
